// Profiling type definitions

export enum ProfileType {
  CPU = 'cpu',
  HEAP = 'heap',
  GOROUTINE = 'goroutine',
  BLOCK = 'block',
  MUTEX = 'mutex',
  ALLOCS = 'allocs',
  TRACE = 'trace',
}

export enum ProfileStatus {
  PENDING = 'pending',
  COLLECTING = 'collecting',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export interface Profile {
  id: string;
  profile_type: ProfileType;
  status: ProfileStatus;
  file_size_bytes: number;
  duration_seconds: number;
  storage_key: string;
  created_by: string;
  description?: string;
  created_at: string;
  expires_at: string;
  download_url?: string;
}

export interface ProfileListing {
  items: Profile[];
  total: number;
  limit: number;
  offset: number;
}

export interface CollectProfileRequest {
  profile_type: ProfileType;
  duration_seconds?: number;
  description?: string;
}

export interface FunctionStats {
  function_name: string;
  package_name: string;
  flat_value: number;
  flat_percent: number;
  cum_value: number;
  cum_percent: number;
}

export interface ProfileAlert {
  severity: 'info' | 'warning' | 'critical';
  message: string;
  metric: string;
  threshold: number;
  current_value: number;
}

export interface ProfileAnalysis {
  profile_id: string;
  top_functions: FunctionStats[];
  hotspots: string[];
  insights: string[];
  alerts: ProfileAlert[];
}

export interface FunctionDiff {
  function_name: string;
  base_value: number;
  compare_value: number;
  diff_percent: number;
  trend: 'improved' | 'degraded' | 'unchanged';
}

export interface CompareProfilesRequest {
  base_profile_id: string;
  compare_profile_id: string;
}

export interface ProfileComparison {
  base_profile: Profile;
  compare_profile: Profile;
  diff_functions: FunctionDiff[];
  summary: string;
}

export interface AlertThresholds {
  goroutine_count: number;
  heap_alloc_mb: number;
  cpu_percent: number;
}

export interface ProfilingConfig {
  enabled: boolean;
  scheduled_enabled: boolean;
  schedule_cron: string;
  cpu_sample_rate_hz: number;
  retention_days: number;
  auto_analyze: boolean;
  alert_thresholds: AlertThresholds;
}

export interface RuntimeStats {
  goroutine_count: number;
  heap_alloc_mb: number;
  heap_sys_mb: number;
  heap_object_count: number;
  stack_in_use_mb: number;
  num_gc: number;
  gc_pause_ns: number;
  num_cpu: number;
}

export interface ListProfilesParams {
  limit?: number;
  offset?: number;
  profile_type?: ProfileType;
  from_date?: string;
  to_date?: string;
}
